import { graphqlClient } from '@/lib/graphql-client';
import { INSERT_CAMPAIGN_MUTATION, UPDATE_CAMPAIGN_MUTATION } from '../graphql/mutations';
import { GET_CAMPAIGNS_QUERY } from '../graphql/queries';
import type {
  CampaignInsertInput,
  CampaignItem,
  CampaignsPagingResult,
  GetCampaignsParams,
  InsertCampaignResult,
  UpdateCampaignParams,
  UpdateCampaignResult,
} from '../types/campaigns.types';
import { buildCloneCampaignInput } from '../utils/campaigns-mapper.utils';

type GetCampaignsResponse = {
  getCampaigns?: CampaignsPagingResult;
};

type InsertCampaignResponse = {
  insertCampaign?: InsertCampaignResult;
};

type UpdateCampaignResponse = {
  updateCampaign?: UpdateCampaignResult;
};

export const getCampaigns = async (
  params: GetCampaignsParams = {}
): Promise<CampaignsPagingResult> => {
  const {
    where = {},
    order = [{ field: 'CreatedDate', direction: 'DESC' }],
    paging = { pageNo: 1, pageSize: 10 },
  } = params;

  const response = await graphqlClient.query<GetCampaignsResponse>({
    query: GET_CAMPAIGNS_QUERY,
    variables: {
      where,
      order,
      paging,
    },
  });

  const campaigns = response?.getCampaigns;

  if (!campaigns || typeof campaigns !== 'object') {
    throw new Error('Failed to load campaigns: invalid response structure');
  }

  return {
    items: Array.isArray(campaigns.items) ? campaigns.items : [],
    totalCount: Number(campaigns.totalCount ?? 0),
    pageNo: Number(campaigns.pageNo ?? paging.pageNo ?? 1),
    pageSize: Number(campaigns.pageSize ?? paging.pageSize ?? 10),
    totalPages: Number(campaigns.totalPages ?? 0),
    hasNextPage: Boolean(campaigns.hasNextPage),
    hasPreviousPage: Boolean(campaigns.hasPreviousPage),
  };
};

export const getCampaignById = async (campaignId: string): Promise<CampaignItem> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const result = await getCampaigns({
    where: { ItemId: { eq: id } },
    paging: { pageNo: 1, pageSize: 1 },
  });

  const campaign = result.items[0];
  if (!campaign) {
    throw new Error('Campaign not found');
  }

  return campaign;
};

export const addCampaign = async (input: CampaignInsertInput): Promise<InsertCampaignResult> => {
  const response = await graphqlClient.mutate<InsertCampaignResponse>({
    query: INSERT_CAMPAIGN_MUTATION,
    variables: { input },
  });

  const result = response?.insertCampaign;
  if (!result || typeof result !== 'object') {
    throw new Error('Failed to create campaign: invalid response structure');
  }

  const itemId = String(result.itemId ?? '').trim();
  if (!result.acknowledged && !itemId) {
    throw new Error(result.message || 'Failed to create campaign');
  }

  return result;
};

export const updateCampaign = async (
  params: UpdateCampaignParams
): Promise<UpdateCampaignResult> => {
  const response = await graphqlClient.mutate<UpdateCampaignResponse>({
    query: UPDATE_CAMPAIGN_MUTATION,
    variables: {
      where: params.where,
      input: params.input,
    },
  });

  const result = response?.updateCampaign;
  if (!result || typeof result !== 'object') {
    throw new Error('Failed to update campaign: invalid response structure');
  }

  const itemId = String(result.itemId ?? '').trim();
  if (!result.acknowledged && !itemId) {
    throw new Error(result.message || 'Failed to update campaign');
  }

  return {
    acknowledged: Boolean(result.acknowledged),
    itemId,
    totalImpactedData: Number(result.totalImpactedData ?? 0),
    message: result.message,
  };
};

export const duplicateCampaignById = async (campaignId: string): Promise<InsertCampaignResult> => {
  const source = await getCampaignById(campaignId);
  return addCampaign(buildCloneCampaignInput(source));
};

export const cancelCampaignById = async (campaignId: string): Promise<UpdateCampaignResult> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  return updateCampaign({
    where: { ItemId: { eq: id } },
    input: {
      Status: 'Draft',
      IsArchived: false,
    },
  });
};
